import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { ContentMasterOtherSourceStatusView } from '../../api/generated/models/ContentMasterOtherSourceStatusView';
import { ContentMasterProductView } from '../../api/generated/models/ContentMasterProductView';
import {
  contentMasterStatusLabelKey,
  getContentMasterStatusClassName,
} from '../../utils/contentMasterProcessingStatus';

type ContentMasterOtherSourcesBadgeProps = {
  /**
   * Statuses of the SAME Wawi article on OTHER sources within the current data
   * source (`ContentMasterProductView.otherSourceProcessing`). Empty / nullish
   * → nothing is rendered.
   */
  items?: Array<ContentMasterOtherSourceStatusView> | null;
  /** Maps a raw `source` literal (e.g. `shop:1`) to a human label. */
  resolveSourceLabel: (source: string) => string;
};

type ResolvedEntry = {
  source: string;
  processingStatus?: ContentMasterOtherSourceStatusView.processingStatus;
};

/**
 * Renders a small `+N` pill next to the product status pill, where `N` is the
 * number of OTHER sources the same Wawi article is already being processed on.
 * Hovering / focusing the pill opens a tooltip listing each source and its
 * status. The tooltip is rendered through a portal with fixed positioning so it
 * is never clipped by the list table's `overflow: hidden`.
 */
const ContentMasterOtherSourcesBadge: React.FC<ContentMasterOtherSourcesBadgeProps> = ({
  items,
  resolveSourceLabel,
}) => {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const entries: ResolvedEntry[] = (items ?? []).flatMap((entry) =>
    typeof entry?.source === 'string' && entry.source.length > 0
      ? [{ source: entry.source, processingStatus: entry.processingStatus }]
      : []
  );

  const open = useCallback(() => {
    const el = triggerRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    setCoords({ top: rect.bottom + 8, left: rect.left + rect.width / 2 });
  }, []);

  const close = useCallback(() => setCoords(null), []);

  if (entries.length === 0) {
    return null;
  }

  return (
    <span
      ref={triggerRef}
      className="content-master-other-sources"
      tabIndex={0}
      role="button"
      aria-label={t('content_master_other_sources_aria', { count: entries.length })}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
    >
      <span className="content-master-other-sources__badge">+{entries.length}</span>
      {coords &&
        createPortal(
          <span
            className="content-master-other-sources__tooltip"
            role="tooltip"
            style={{ top: coords.top, left: coords.left }}
          >
            <span className="content-master-other-sources__tooltip-title">
              {t('content_master_other_sources_tooltip_title')}
            </span>
            <span className="content-master-other-sources__list">
              {entries.map((entry) => (
                <span className="content-master-other-sources__item" key={entry.source}>
                  <span className="content-master-other-sources__source">
                    {resolveSourceLabel(entry.source)}
                  </span>
                  <span
                    className={getContentMasterStatusClassName(
                      entry.processingStatus as unknown as ContentMasterProductView.processingStatus
                    )}
                  >
                    {t(
                      contentMasterStatusLabelKey(
                        entry.processingStatus as unknown as ContentMasterProductView.processingStatus
                      )
                    )}
                  </span>
                </span>
              ))}
            </span>
          </span>,
          document.body
        )}
    </span>
  );
};

export default ContentMasterOtherSourcesBadge;
